import React, { memo, useCallback, useMemo, useState } from "react";
import { useAbba } from "@foundation/abba-client";
import MediaServicesSearchClient from "@foundation/media-services-search-client";
import { LineSearchDropdown } from "@bloomberg/media-ui-dropdown";
import type { LineSearchDropdownProps } from "@bloomberg/media-ui-dropdown/types";
import { classes } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { debounce } from "@foundation/util";
import styles from "./styles.module.css";
import type { SearchQueryResult } from "~types/types";
import { getSessionIdCookie } from "~utils/sessionUtils";
import { useTranslateMessage } from "~utils/i18n";

export type DropdownOptions = LineSearchDropdownProps["options"];

export type TickerSearchSearchProps = {
  searchURL: string;
  className?: string;
  countryCode?: string;
  disabled?: boolean;
  placeholder?: string;
  onChange: (
    value: DropdownOptions[0] | null,
    downshiftHandle: { setState: (value: unknown) => void }
  ) => void;
};

const MemoizedLineSearchDropdown = memo(LineSearchDropdown);
const INPUT_DELAY = 300;

export default function TickerSearch(props: TickerSearchSearchProps) {
  const {
    searchURL,
    className,
    countryCode,
    disabled = false,
    placeholder,
    onChange,
  } = props;

  const { abbaConfig } = useAbba();

  const enableFuzziness =
    abbaConfig?.["watchlist-search"]?.enableFuzziness ?? false;

  const [suggestions, setSuggestions] = useState<DropdownOptions>([]);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  const searchClient = useMemo(
    () =>
      new MediaServicesSearchClient({
        baseUrl: searchURL,
      }),
    [searchURL]
  );

  const fetchSearchResults = useCallback(
    (searchQuery: string) => {
      if (!searchQuery) return undefined;

      return searchClient.lookup({
        query: searchQuery,
        sites: ["bbiz"],
        includeBlocklisted: false,
        source: getSessionIdCookie(),
        size: 100,
        country: countryCode,
        types: [
          "company_public",
          "Index",
          "Fund",
          "Currency",
          "Commodity",
          "Bond",
        ],
        boostParents: true,
        ...(enableFuzziness && {
          fuzziness: "auto",
          popularity: "watchlist",
        }),
      });
    },
    [searchClient, enableFuzziness, countryCode]
  );

  const debouncedFetchResults = debounce((searchQuery: string) => {
    if (!searchQuery) clearSuggestions();
    trackClickEvent(
      "ticker search",
      searchQuery,
      "user entry",
      "ticker search"
    );

    fetchSearchResults?.(searchQuery)?.then(
      ({ results: nextSuggestions }: SearchQueryResult) => {
        if (!nextSuggestions) {
          clearSuggestions();

          return;
        }

        setSuggestions(
          nextSuggestions.map(({ name, resource_id: resourceId }) => ({
            value: resourceId,
            subText: name,
          }))
        );
      }
    );
  }, INPUT_DELAY);

  const translate = useTranslateMessage();

  const translations = {
    noResults: translate({
      id: "noResults",
      defaultMessage: "No Results",
    }),
    addSymbol: translate({
      id: "addSymbol",
      defaultMessage: "Add Symbol...",
    }),
  };

  return (
    <MemoizedLineSearchDropdown
      className={classes([styles.tickerSearch, styles.autocomplete, className])}
      onTextChange={debouncedFetchResults}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onChange={onChange}
      id="autocomplete"
      inputProps={{ autocomplete: "off" }}
      variant="default"
      dropdownVariant="markets"
      showDropdownPlusBtn
      disabled={disabled}
      placeholder={placeholder ?? translations.addSymbol}
      noOptionsText={translations.noResults}
      options={suggestions}
    />
  );
}
